import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// --- Helper Components ---
const DatePartSelector = ({ name, value, onChange, options, placeholder }) => (
  <select name={name} value={value} onChange={onChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
    <option value="">{placeholder}</option>
    {options.map(opt => <option key={opt} value={opt}>{opt}</option>)
    }
  </select>
);

const DateSelector = ({ prefix, values, onChange, t, years }) => {
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const days = Array.from({ length: 31 }, (_, i) => i + 1);

    return (
        <div className="grid grid-cols-3 gap-2">
            <DatePartSelector name={`${prefix}Year`} value={values[`${prefix}Year`]} onChange={onChange} options={years} placeholder={t.year} />
            <DatePartSelector name={`${prefix}Month`} value={values[`${prefix}Month`]} onChange={onChange} options={months} placeholder={t.month} />
            <DatePartSelector name={`${prefix}Day`} value={values[`${prefix}Day`]} onChange={onChange} options={days} placeholder={t.day} />
        </div>
    );
};

function History() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lang, setLang] = useState('en');
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [userToken, setUserToken] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  
  const [searchInputs, setSearchInputs] = useState({
    startYear: '', startMonth: '', startDay: '',
    endYear: '', endMonth: '', endDay: '',
    customerId: '',
    status: ''
  });
  const [activeFilters, setActiveFilters] = useState({});

  const translations = useMemo(() => ({
    ja: {
      mainTitle: "請求履歴",
      loading: "履歴を読み込み中...",
      loadMore: "次の50件を読み込む",
      loadingMore: "読み込み中...",
      noMoreData: "これ以上データはありません。",
      noHistory: "請求履歴はありません。",
      thTimestamp: "請求日時",
      thCustomerId: "顧客ID",
      thAmount: "請求金額",
      thStatus: "結果",
      thError: "エラー詳細",
      success: "成功",
      failure_no_retry: "失敗",
      authLoading: "認証情報を読み込んでいます...",
      authRequired: "履歴にアクセスするにはログインしてください。",
      search: "検索",
      reset: "リセット",
      startDate: "開始日",
      endDate: "終了日",
      all: "すべて",
      year: "年",
      month: "月",
      day: "日",
    },
    en: {
      mainTitle: "Billing History",
      loading: "Loading history...",
      loadMore: "Load Next 50",
      loadingMore: "Loading...",
      noMoreData: "No more data.",
      noHistory: "No billing history found.",
      thTimestamp: "Billing Date",
      thCustomerId: "Customer ID",
      thAmount: "Amount",
      thStatus: "Result",
      thError: "Error Details",
      success: "Success",
      failure_no_retry: "Failure",
      authLoading: "Loading authentication...",
      authRequired: "Please log in to access history.",
      search: "Search",
      reset: "Reset",
      startDate: "Start Date",
      endDate: "End Date",
      all: "All",
      year: "Year",
      month: "Month",
      day: "Day",
    },
  }), []);

  const t = translations[lang];
  const years = useMemo(() => {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);
  }, []);

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    try {
        const date = new Date(isoString);
        return date.toLocaleString(lang === 'ja' ? 'ja-JP' : 'en-US', { timeZone: 'Asia/Tokyo', hour12: false });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const formatAmount = (amount) => {
      if (amount === null || amount === undefined) return '-';
      return amount.toLocaleString('ja-JP', { style: 'currency', currency: 'JPY' });
  }

  const formatStatus = (result) => {
      switch(result) {
          case 'OK': return t.success;
          case 'NG_RETRY': return t.failure_no_retry;
          case 'NG_NO_RETRY': return t.failure_no_retry;
          default: return result;
      }
  }

  useEffect(() => {
    const userLang = navigator.language.startsWith('ja') ? 'ja' : 'en';
    setLang(userLang);
    document.title = "BillWave";
  }, []);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    if (!userToken) {
      throw new Error('No user token available. Please log in.');
    }
    const newOptions = { ...options };
    newOptions.headers = { ...newOptions.headers, Authorization: `Bearer ${userToken}` };
    const response = await fetch(url, newOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let error;
      try {
        const errorJson = JSON.parse(errorText);
        error = new Error(errorJson.error || `Request failed with status ${response.status}`);
      } catch (e) {
        error = new Error(errorText || `Request failed with status ${response.status}`);
      }
      throw error;
    }
    return response;
  }, [userToken]);

  const fetchHistory = useCallback(async (cursor, filters) => {
    if (!authInitialized || !userToken) return;

    if (cursor === null) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = new URLSearchParams();
      if (cursor) params.append('cursor', cursor);
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
            if (value) params.append(key, value);
        });
      }

      const url = `/v1/logs?${params.toString()}`;
      const response = await fetchWithAuth(url);
      const data = await response.json();

      setHistory(prev => cursor ? [...prev, ...data.logs] : data.logs);
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);

    } catch (error) {
       if (error.name !== 'AbortError') {
        console.error('Fetch error:', error.message);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [authInitialized, userToken, fetchWithAuth]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setUserToken(token);
        } catch (error) {
          console.error("Error getting user token:", error);
          setUserToken(null);
        }
      } else {
        setUserToken(null);
      }
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authInitialized) {
      fetchHistory(null, {});
    }
  }, [authInitialized, fetchHistory]);

  const handleSearchInputChange = (e) => {
    setSearchInputs({ ...searchInputs, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    const filters = {};
    if (searchInputs.customerId) filters.customerId = searchInputs.customerId;
    if (searchInputs.status) filters.status = searchInputs.status;

    if (searchInputs.startYear && searchInputs.startMonth && searchInputs.startDay) {
        filters.startDate = `${searchInputs.startYear}-${String(searchInputs.startMonth).padStart(2, '0')}-${String(searchInputs.startDay).padStart(2, '0')}`;
    }
    if (searchInputs.endYear && searchInputs.endMonth && searchInputs.endDay) {
        filters.endDate = `${searchInputs.endYear}-${String(searchInputs.endMonth).padStart(2, '0')}-${String(searchInputs.endDay).padStart(2, '0')}`;
    }
    setActiveFilters(filters);
    fetchHistory(null, filters);
  };

  const handleReset = () => {
    setSearchInputs({ 
        startYear: '', startMonth: '', startDay: '',
        endYear: '', endMonth: '', endDay: '',
        customerId: '', 
        status: '' 
    });
    setActiveFilters({});
    fetchHistory(null, {});
  };

  const handleLoadMore = () => {
    if (nextCursor && hasMore) {
      fetchHistory(nextCursor, activeFilters);
    }
  };

  if (!authInitialized) {
    return <div className="flex justify-center items-center h-screen"><p className="text-xl text-gray-500">{t.authLoading}</p></div>;
  }

  if (!userToken) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">{t.authRequired}</h1>
          </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 w-full max-w-6xl mx-auto my-8 shadow-lg rounded-lg">
        <div className="flex justify-between items-center mb-6 pb-2 border-b-2 border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900">{t.mainTitle}</h1>
        </div>
      
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.startDate}</label>
                <DateSelector prefix="start" values={searchInputs} onChange={handleSearchInputChange} t={t} years={years} />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.endDate}</label>
                <DateSelector prefix="end" values={searchInputs} onChange={handleSearchInputChange} t={t} years={years} />
            </div>
            <div>
                <label htmlFor="customerId" className="block text-sm font-medium text-gray-700">{t.thCustomerId}</label>
                <input type="text" name="customerId" id="customerId" value={searchInputs.customerId} onChange={handleSearchInputChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>
            <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">{t.thStatus}</label>
                <select name="status" id="status" value={searchInputs.status} onChange={handleSearchInputChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                    <option value="">{t.all}</option>
                    <option value="OK">{t.success}</option>
                    <option value="NG_NO_RETRY">{t.failure_no_retry}</option>
                </select>
            </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={handleSearch} className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md">{t.search}</button>
          <button onClick={handleReset} className="bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors shadow-md">{t.reset}</button>
        </div>
      </div>

      {loading ? (
        <p className="text-center py-4">{t.loading}</p>
      ) : history.length === 0 ? (
        <p className="text-center py-4">{t.noHistory}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse table-auto">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left whitespace-nowrap">{t.thTimestamp}</th>
                  <th className="border p-2 text-left">{t.thCustomerId}</th>
                  <th className="border p-2 text-left">{t.thAmount}</th>
                  <th className="border p-2 text-left whitespace-nowrap">{t.thStatus}</th>
                  <th className="border p-2 text-left">{t.thError}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="border p-2 whitespace-nowrap">{formatDate(item.processed_at)}</td>
                    <td className="border p-2 break-all">{item.customer_id}</td>
                    <td className="border p-2">{formatAmount(item.total_amount)}</td>
                    <td className={`border p-2 whitespace-nowrap font-semibold ${item.result === 'OK' ? 'text-green-600' : 'text-red-600'}`}>{formatStatus(item.result)}</td>
                    <td className="border p-2 break-all">
                      {item.error_code && (
                        <span className="inline-block bg-gray-200 text-gray-800 text-xs px-2 py-1 rounded mr-2 font-mono">
                          {item.error_code}
                        </span>
                      )}
                      <span>{item.error_reason || ''}</span>
                      {item.stripe_detail && (
                        <div className="text-xs text-gray-500 mt-1 italic">
                          Stripe詳細: {item.stripe_detail}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="flex justify-center mt-6">
              {hasMore && (
                  <button onClick={handleLoadMore} disabled={loadingMore} className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md disabled:bg-blue-400 disabled:cursor-not-allowed">
                      {loadingMore ? t.loadingMore : t.loadMore}
                  </button>
              )}
              {!hasMore && history.length > 0 && (
                  <p className="text-center text-lg text-gray-500">{t.noMoreData}</p>
              )}
          </div>
        </>
      )}
    </div>
  );
}

export default History;